import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lightbulb, ChefHat, Camera, Calculator, ShoppingCart, ArrowRight, Utensils, Warehouse, TrendingUp, Users, Quote, Star } from "lucide-react";
import { Emblem, LogoFull } from "@/components/Logo";
import { IMG } from "@/constants/images";
import { useAuth } from "@/context/AuthContext";
import { InstallAppButton } from "@/components/InstallApp";

const NAVLINKS = ["How It Works", "For Chefs", "For Restaurants", "Pricing"];

const TESTIMONIALS = [
  { quote: "I typed in \u201clate-summer coastal tasting, seven courses\u201d and had plated visuals and full recipe cards in under two minutes. Plate Studio nailed the composition from the actual ingredients \u2014 my line cooks plated straight off the map.",
    name: "Marcus De León", role: "Executive Chef · Lumen, Miami" },
  { quote: "The Plate Cost Analysis is the reason we renew. Food cost on every dish, snapshot pricing, and the target-cost optimizer pays for the whole subscription in one menu revision.",
    name: "Priya Natarajan", role: "Chef-Owner · Saffron House" },
  { quote: "I dropped in our Sysco Order Guide and every ingredient re-priced itself. Two days later a price-change alert flagged that our short rib dish had blown past target \u2014 caught it before it ate our margin.",
    name: "Daniel Okafor", role: "Culinary Director · Ember Group" },
  { quote: "Import My Recipe scaled our grandmother's braise from 4 covers to 120 and cleaned up the method. Then I hit Export and handed the kitchen branded prep sheets with our own logo on them.",
    name: "Sofia Bianchi", role: "Head Chef · Trattoria Nove" },
  { quote: "Profit Mode is wild \u2014 I set a $42 sell price and a 26% food cost and it engineered a dish backward to hit it. One tap on Pair Courses gave every plate a cocktail and a zero-proof option.",
    name: "Jerome Blanchard", role: "Beverage & Culinary Lead · The Atrium" },
  { quote: "Running three kitchens, role permissions let my sous chefs build menus while only my exec team touches cost and branding. Kitchen Mode on a tablet is what finally got service off paper.",
    name: "Alana Reyes", role: "Group Executive Chef · Costa Hospitality" },
];

const FLOW = [
  { label: "IDEA", icon: Lightbulb, desc: "Describe a vibe, theme or ingredients." },
  { label: "RECIPE", icon: ChefHat, desc: "Coherent, professional recipe cards." },
  { label: "PLATE", icon: Camera, desc: "AI plating built from real ingredients." },
  { label: "COST", icon: Calculator, desc: "Live Plate Cost Analysis & margins." },
  { label: "ORDER", icon: ShoppingCart, desc: "Pack-size aware purchasing lists." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h);
  }, []);
  const start = () => nav(user ? "/dashboard" : "/signup");

  return (
    <div className="bg-ink text-cream min-h-screen">
      {/* Header */}
      <header className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "glass" : ""}`}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-20">
          <Link to="/" className="flex items-center gap-2.5">
            <Emblem className="h-9 w-9" />
            <span className="font-serif text-xl gold-text tracking-wider">CHEF PLATED</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAVLINKS.map((l) => (
              <a key={l} href={l === "Pricing" ? "/pricing" : `#${l.toLowerCase().replace(/ /g, "-")}`}
                className="text-sm text-white/60 hover:text-gold transition-colors">{l}</a>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <InstallAppButton className="hidden sm:flex text-sm text-white/70 hover:text-gold px-3 py-2 items-center gap-2" label="Download App" />
            <InstallAppButton className="flex sm:hidden text-white/70 hover:text-gold p-2 items-center justify-center" label="" />
            <Link to="/login" data-testid="header-login" className="text-sm text-white/70 hover:text-white px-2 sm:px-3 py-2">Login</Link>
            <button onClick={start} data-testid="header-start-plating"
              className="btn-gold px-4 sm:px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm whitespace-nowrap">Start Plating</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="label-eyebrow mb-6">From Inspiration to Execution</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight">
              FROM<br />INSPIRATION<br /><span className="gold-text">TO PLATE.</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-7 text-lg text-white/70 max-w-xl leading-relaxed">
              Turn your ingredients, ideas and culinary vision into complete recipes, stunning plating concepts,
              precise food-cost analysis and purchase-ready ingredient lists.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-wrap gap-4">
              <button onClick={start} data-testid="hero-start-plating"
                className="btn-gold px-8 py-4 text-sm uppercase tracking-widest rounded-sm flex items-center gap-2">
                Start Plating <ArrowRight size={16} />
              </button>
              <a href="#how-it-works" data-testid="hero-watch-demo"
                className="btn-outline-gold px-8 py-4 text-sm uppercase tracking-widest rounded-sm">Watch Demo</a>
            </motion.div>
          </div>
          {/* Flow card */}
          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
            className="hidden lg:block">
            <div className="matte-card p-8 gold-border relative">
              <p className="label-eyebrow mb-6">The Chef Plated Workflow</p>
              <div className="space-y-1">
                {FLOW.map((f, i) => (
                  <React.Fragment key={f.label}>
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + i * 0.15 }}
                      className="flex items-center gap-4 p-3 rounded-sm hover:bg-white/5">
                      <div className="h-11 w-11 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center text-gold shrink-0">
                        <f.icon size={20} strokeWidth={1.6} />
                      </div>
                      <div>
                        <p className="font-mono text-gold text-sm tracking-wide">{f.label}</p>
                        <p className="text-white/50 text-sm">{f.desc}</p>
                      </div>
                    </motion.div>
                    {i < FLOW.length - 1 && <div className="ml-8 h-4 w-px bg-gradient-to-b from-gold/40 to-transparent" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="py-24 border-y border-white/5" id="product">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-6xl leading-tight">
            <span className="gold-text">IMAGINE IT.</span> PLATE IT.<br />
            <span className="gold-text">COST IT.</span> ORDER IT.
          </h2>
          <p className="mt-6 text-white/60 text-lg max-w-2xl mx-auto">
            Not a recipe generator — an intelligent culinary command center that connects development,
            costing, plating, purchasing and kitchen execution into one seamless ecosystem.
          </p>
        </div>
      </section>

      {/* How it works flow */}
      <section className="py-24" id="how-it-works">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="label-eyebrow text-center mb-3">How It Works</p>
          <h2 className="font-serif text-4xl md:text-5xl text-center mb-16">One idea. The entire line.</h2>
          <div className="grid md:grid-cols-5 gap-4">
            {FLOW.map((f, i) => (
              <motion.div key={f.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="matte-card p-6 text-center">
                <div className="mx-auto h-14 w-14 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center text-gold mb-4">
                  <f.icon size={24} strokeWidth={1.5} />
                </div>
                <p className="font-mono text-gold text-sm">{`0${i + 1}`}</p>
                <p className="font-serif text-2xl mt-1">{f.label}</p>
                <p className="text-white/50 text-sm mt-2">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature bento */}
      <section className="py-16" id="for-restaurants">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-3 gap-4">
          <FeatureCard span="md:col-span-2 md:row-span-2" img={IMG.shortRib} icon={Camera}
            title="Plate Studio"
            body="Every recipe gets an AI-generated plating visual built from its actual ingredients and preparation — styles from Classic to Avant-Garde, with a step-by-step plating map." tall />
          <FeatureCard img={IMG.scallops} icon={Calculator} title="Plate Cost Analysis"
            body="Reproducible PCA on every dish. Snapshot pricing, food-cost %, margins and target-cost optimization." />
          <FeatureCard img={IMG.ingredientsDark} icon={ShoppingCart} title="Purchasing Engine"
            body="Pack-size-aware lists. Connect Sysco to match SKUs and build a supplier cart." />
          <FeatureCard img={IMG.warmingLamp} icon={Warehouse} title="My Kitchen"
            body="Inventory intelligence — value, low-stock, expiry & waste risk. Turn inventory into a menu." />
          <FeatureCard img={IMG.kitchenTablet} icon={Utensils} title="Kitchen Mode"
            body="Tablet-first prep sheets, station assignments and scaled quantities for service." />
          <FeatureCard img={IMG.saucePour} icon={TrendingUp} title="Profit Mode"
            body="Reverse the question. Tell Chef Plated what you want to earn — it engineers the dish." />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 border-t border-white/5" id="testimonials" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="label-eyebrow text-gold/70 flex items-center gap-2"><Star size={13} className="fill-gold text-gold" /> From the pass</p>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight mt-3">Chefs are already running service on it.</h2>
            <p className="text-white/55 mt-4 text-lg">Real kitchens using Plate Studio, Plate Cost Analysis, Order Guide pricing and more.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} data-testid={`testimonial-${i}`}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="matte-card p-7 flex flex-col hover:border-gold/30 transition-colors duration-300">
                <Quote size={26} className="text-gold/40 mb-4" strokeWidth={1.5} />
                <div className="flex gap-0.5 mb-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={13} className="fill-gold text-gold" />)}
                </div>
                <p className="text-white/80 leading-relaxed flex-1 text-[15px]">"{t.quote}"</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <p className="font-serif text-lg gold-text leading-tight">{t.name}</p>
                  <p className="text-white/45 text-sm mt-0.5">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 relative grain" id="for-chefs">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Emblem className="h-16 w-16 mx-auto mb-8" />
          <h2 className="font-serif text-4xl md:text-6xl leading-tight">
            Built for professional chefs.
          </h2>
          <p className="mt-5 text-white/60 text-lg">
            From "I have an idea" to "I know exactly how to cook it, plate it, price it, purchase it and execute it."
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <button onClick={start} data-testid="cta-start-plating"
              className="btn-gold px-10 py-4 text-sm uppercase tracking-widest rounded-sm">Start Plating</button>
            <Link to="/pricing" className="btn-outline-gold px-10 py-4 text-sm uppercase tracking-widest rounded-sm">View Pricing</Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5"><Emblem className="h-8 w-8" />
            <span className="font-serif gold-text tracking-wider">CHEF PLATED</span></div>
          <p className="text-white/40 text-sm">From Inspiration to Execution · Imagine It. Plate It. Cost It. Order It.</p>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/terms" className="text-white/50 hover:text-gold" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="text-white/50 hover:text-gold" data-testid="footer-privacy">Privacy</Link>
            <Link to="/data" className="text-white/50 hover:text-gold" data-testid="footer-data">Data</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ img, icon: Icon, title, body, span = "", tall = false }) {
  return (
    <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`matte-card overflow-hidden group relative ${span}`}>
      <div className={`relative ${tall ? "h-72 md:h-96" : "h-44"} overflow-hidden`}>
        <img src={img} alt={title} className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/40 to-transparent" />
      </div>
      <div className="p-6 relative -mt-10">
        <div className="h-11 w-11 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center text-gold mb-3">
          <Icon size={20} strokeWidth={1.6} />
        </div>
        <h3 className="font-serif text-2xl">{title}</h3>
        <p className="text-white/55 text-sm mt-2 leading-relaxed">{body}</p>
      </div>
    </motion.div>
  );
}
